import { queueOfflineOperation, type OfflineQueueRecord } from './offline-store';
import { decideNavioBuildOfflineWrite } from './offline-write-policy';

import type {
  NavioBuildOfflineEntity,
  NavioBuildQueuedOperation,
} from '../../../../../packages/shared/src/types/naviobuild-offline';


export interface NavioBuildMutationPolicy {
  procedure: string;
  routeId: string;
  entity: NavioBuildOfflineEntity;
  operation: NavioBuildQueuedOperation['operation'];
}

const policy = (
  procedure: string,
  routeId: string,
  entity: NavioBuildOfflineEntity,
  operation: NavioBuildQueuedOperation['operation']
): NavioBuildMutationPolicy => ({ procedure, routeId, entity, operation });

export const NAVIOBUILD_MUTATION_POLICIES = [
  policy('build.projects.create', 'overview', 'project', 'create'),
  policy('build.projects.update', 'setup', 'project', 'update'),
  policy('build.tracker.create', 'tracker', 'tracker_item', 'create'),
  policy('build.tracker.update', 'tracker', 'tracker_item', 'update'),
  policy('build.tracker.archive', 'tracker', 'tracker_item', 'update'),
  policy('build.lists.create', 'lists', 'list_setting', 'create'),
  policy('build.lists.update', 'lists', 'list_setting', 'update'),
  policy('build.lists.archive', 'lists', 'list_setting', 'update'),
  policy('build.workItems.create', 'schedule', 'schedule', 'create'),
  policy('build.workItems.update', 'schedule', 'schedule', 'update'),
  policy('build.workItems.delete', 'schedule', 'schedule', 'delete'),
  policy('build.workItems.addDependency', 'schedule', 'schedule', 'create'),
  policy('build.workItems.removeDependency', 'schedule', 'schedule', 'delete'),
  policy('build.milestones.create', 'schedule', 'milestone', 'create'),
  policy('build.milestones.update', 'schedule', 'milestone', 'update'),
  policy('build.milestones.delete', 'schedule', 'milestone', 'delete'),
  policy('build.ddr.create', 'ddr', 'daily_report', 'create'),
  policy('build.ddr.update', 'ddr', 'daily_report', 'update'),
  policy('build.ddr.delete', 'ddr', 'daily_report', 'delete'),
  policy('build.ddr.importSuggestions', 'ddr', 'daily_report', 'create'),
  policy('build.ddr.archiveHandover', 'ddr', 'daily_report', 'update'),
  policy('build.ddr.createTemplate', 'ddr', 'ddr_template', 'create'),
  policy('build.ddr.importTemplate', 'ddr', 'ddr_template', 'create'),
  policy('build.ddr.exportTemplate', 'ddr', 'ddr_template', 'update'),
  policy('build.ddr.setTemplateStatus', 'ddr', 'ddr_template', 'update'),
  policy('build.ddr.importTemplateJson', 'ddr', 'ddr_template', 'create'),
  policy('build.ddr.addTemplateItem', 'ddr', 'ddr_template', 'create'),
  policy('build.ddr.deleteTemplateItem', 'ddr', 'ddr_template', 'delete'),
  policy('build.drawings.create', 'drawings', 'drawing', 'create'),
  policy('build.drawings.update', 'drawings', 'drawing', 'update'),
  policy('build.drawings.delete', 'drawings', 'drawing', 'delete'),
  policy('build.drawings.setStatus', 'drawings', 'drawing', 'update'),
  policy('build.drawings.fileNewRevision', 'drawings', 'drawing', 'attach_file'),
  policy('build.drawings.linkNode', 'drawings', 'drawing', 'update'),
  policy('build.attachments.upload', 'quality', 'file_asset', 'attach_file'),
  policy('build.attachments.delete', 'quality', 'file_asset', 'detach_file'),
  policy('build.ncrs.create', 'quality', 'quality_item', 'create'),
  policy('build.ncrs.update', 'quality', 'quality_item', 'update'),
  policy('build.ncrs.delete', 'quality', 'quality_item', 'delete'),
  policy('build.snags.create', 'quality', 'quality_item', 'create'),
  policy('build.snags.update', 'quality', 'quality_item', 'update'),
  policy('build.snags.delete', 'quality', 'quality_item', 'delete'),
  policy('build.regulatory.updateVesselSetting', 'regulatory', 'regulatory_deliverable', 'update'),
  policy('build.regulatory.upsertSignoff', 'regulatory', 'regulatory_deliverable', 'update'),
  policy('build.regulatory.updateCatalogReview', 'regulatory', 'regulatory_deliverable', 'update'),
  policy('build.payments.create', 'payments', 'payment', 'create'),
  policy('build.payments.update', 'payments', 'payment', 'update'),
  policy('build.payments.delete', 'payments', 'payment', 'delete'),
  policy('build.variations.create', 'variations', 'variation', 'create'),
  policy('build.variations.update', 'variations', 'variation', 'update'),
  policy('build.variations.delete', 'variations', 'variation', 'delete'),
  policy('build.budget.create', 'budget', 'budget', 'create'),
  policy('build.budget.update', 'budget', 'budget', 'update'),
  policy('build.budget.delete', 'budget', 'budget', 'delete'),
  policy('build.contracts.create', 'foundation', 'contract', 'create'),
  policy('build.contracts.update', 'foundation', 'contract', 'update'),
  policy('build.contracts.delete', 'foundation', 'contract', 'delete'),
  policy('build.correspondence.create', 'records', 'correspondence', 'create'),
  policy('build.correspondence.update', 'records', 'correspondence', 'update'),
  policy('build.correspondence.delete', 'records', 'correspondence', 'delete'),
  policy('build.osi.create', 'osi', 'owner_supplied_item', 'create'),
  policy('build.osi.update', 'osi', 'owner_supplied_item', 'update'),
  policy('build.osi.delete', 'osi', 'owner_supplied_item', 'delete'),
  policy('build.warranty.create', 'warranty', 'warranty', 'create'),
  policy('build.warranty.update', 'warranty', 'warranty', 'update'),
  policy('build.warranty.delete', 'warranty', 'warranty', 'delete'),
  policy('build.positions.create', 'foundation', 'position', 'create'),
  policy('build.positions.update', 'foundation', 'position', 'update'),
  policy('build.positions.delete', 'foundation', 'position', 'delete'),
  policy('build.stakeholders.create', 'foundation', 'stakeholder', 'create'),
  policy('build.stakeholders.update', 'foundation', 'stakeholder', 'update'),
  policy('build.stakeholders.delete', 'foundation', 'stakeholder', 'delete'),
  policy('build.minutes.create', 'records', 'meeting_minutes', 'create'),
  policy('build.minutes.update', 'records', 'meeting_minutes', 'update'),
  policy('build.minutes.delete', 'records', 'meeting_minutes', 'delete'),
  policy('build.visits.create', 'visits', 'site_visit', 'create'),
  policy('build.visits.update', 'visits', 'site_visit', 'update'),
  policy('build.visits.delete', 'visits', 'site_visit', 'delete'),
  policy('build.captures.create', 'visits', 'capture', 'create'),
  policy('build.captures.update', 'visits', 'capture', 'update'),
  policy('build.captures.delete', 'visits', 'capture', 'delete'),
  policy('build.captures.promoteToNcr', 'visits', 'capture', 'update'),
  policy('build.captures.promoteToSnag', 'visits', 'capture', 'update'),
  policy('build.siteVisitBrief.generate', 'brief', 'site_visit_brief', 'create'),
  policy('build.reports.generateOwnerReport', 'reports', 'report', 'create'),
  policy('build.reports.markOwnerReportSent', 'reports', 'report', 'update'),
  policy('build.reports.deleteOwnerReport', 'reports', 'report', 'delete'),
  policy('build.dossierSnapshots.create', 'reports', 'dossier_snapshot', 'create'),
  policy('build.dossierSnapshots.markShared', 'reports', 'dossier_snapshot', 'update'),
  policy('build.dossierSnapshots.updateNotes', 'reports', 'dossier_snapshot', 'update'),
  policy('build.dossierSnapshots.delete', 'reports', 'dossier_snapshot', 'delete'),
  policy('build.handover.complete', 'handover', 'handover', 'update'),
  policy('build.spine.createSystem', 'spine', 'spine', 'create'),
  policy('build.spine.updateSystem', 'spine', 'spine', 'update'),
  policy('build.spine.removeSystem', 'spine', 'spine', 'delete'),
  policy('build.spine.moveSystem', 'spine', 'spine', 'update'),
  policy('build.spine.reorderSystem', 'spine', 'spine', 'update'),
  policy('build.spine.createEquipment', 'spine', 'spine', 'create'),
  policy('build.spine.updateEquipment', 'spine', 'spine', 'update'),
  policy('build.spine.removeEquipment', 'spine', 'spine', 'delete'),
  policy('build.spine.bulkCreate', 'spine', 'spine', 'create'),
  policy('build.certificates.create', 'spine', 'certificate', 'create'),
  policy('build.certificates.update', 'spine', 'certificate', 'update'),
  policy('build.certificates.delete', 'spine', 'certificate', 'delete'),
  policy('build.tests.create', 'spine', 'test_record', 'create'),
  policy('build.tests.update', 'spine', 'test_record', 'update'),
  policy('build.tests.delete', 'spine', 'test_record', 'delete'),
] as const satisfies readonly NavioBuildMutationPolicy[];

export function getNavioBuildMutationPolicy(
  procedure: string
): NavioBuildMutationPolicy | undefined {
  return NAVIOBUILD_MUTATION_POLICIES.find((item) => item.procedure === procedure);
}

export function extractTrpcProcedurePath(mutationKey: unknown): string | null {
  if (!Array.isArray(mutationKey)) return null;

  const parts = mutationKey
    .flat(Number.POSITIVE_INFINITY)
    .filter((part): part is string => typeof part === 'string' && part !== 'mutation');

  return parts.length > 0 ? parts.join('.') : null;
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function recordValue(value: unknown, key: string): unknown {
  return value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined;
}

function entityIdFromVariables(variables: unknown, fallback: string): string {
  return (
    stringValue(recordValue(variables, 'id')) ??
    stringValue(recordValue(variables, 'entityId')) ??
    stringValue(recordValue(variables, 'visitId')) ??
    stringValue(recordValue(variables, 'templateId')) ??
    stringValue(recordValue(variables, 'projectId')) ??
    fallback
  );
}

function projectIdFromVariables(variables: unknown): string | null {
  return stringValue(recordValue(variables, 'projectId'));
}

export function shouldQueueNavioBuildOfflineMutation({
  isOnline,
  error,
  mutationKey,
}: {
  isOnline: boolean;
  error?: unknown;
  mutationKey: unknown;
}): boolean {
  const procedure = extractTrpcProcedurePath(mutationKey);
  return (
    (!isOnline || isRetryableCloudMutationError(error)) &&
    Boolean(procedure && getNavioBuildMutationPolicy(procedure))
  );
}

export function isRetryableCloudMutationError(error: unknown): boolean {
  if (!error) return false;

  const record = error && typeof error === 'object' ? (error as Record<string, unknown>) : {};
  const data =
    record['data'] && typeof record['data'] === 'object'
      ? (record['data'] as Record<string, unknown>)
      : {};
  const code = typeof data['code'] === 'string' ? data['code'].toUpperCase() : '';
  const httpStatus = typeof data['httpStatus'] === 'number' ? data['httpStatus'] : null;
  const message = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();

  return (
    code === 'TIMEOUT' ||
    code === 'INTERNAL_SERVER_ERROR' ||
    code === 'SERVICE_UNAVAILABLE' ||
    httpStatus === 408 ||
    httpStatus === 429 ||
    (httpStatus !== null && httpStatus >= 500) ||
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('timeout') ||
    message.includes('service unavailable') ||
    message.includes('cloud service is temporarily unavailable') ||
    message.includes('database') ||
    message.includes('pooler') ||
    message.includes('postgres')
  );
}

export async function queueNavioBuildOfflineMutation({
  mutationKey,
  variables,
  actorUserId,
}: {
  mutationKey: unknown;
  variables: unknown;
  actorUserId: string;
}): Promise<OfflineQueueRecord | null> {
  const procedure = extractTrpcProcedurePath(mutationKey);
  if (!procedure) return null;

  const mutationPolicy = getNavioBuildMutationPolicy(procedure);
  if (!mutationPolicy) return null;

  const decision = decideNavioBuildOfflineWrite({
    routeId: mutationPolicy.routeId,
    entity: mutationPolicy.entity,
    isOnline: false,
  });

  if (decision.mode === 'block') return null;

  return queueOfflineOperation({
    entity: mutationPolicy.entity,
    entityId: entityIdFromVariables(variables, `local:${procedure}:${Date.now()}`),
    projectId: projectIdFromVariables(variables) ?? 'unknown-project',
    operation: mutationPolicy.operation,
    conflictPolicy: decision.conflictPolicy,
    payload: {
      procedure,
      variables: variables && typeof variables === 'object' ? variables : { value: variables },
      offlineWriteMode: decision.mode,
      queuedAtClient: new Date().toISOString(),
    },
    actorUserId,
  });
}
